import { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Dialog,
  Textarea,
  Tag,
  Space,
  MessagePlugin,
  Select,
  Input,
} from 'tdesign-react';
import { Download } from 'lucide-react';
import {
  CheckIcon,
  CloseIcon,
  SearchIcon,
} from 'tdesign-icons-react';
import { orderApi } from '../../api';
import type { Order, OrderItem, OrderFeeSettleBatchVO } from '../../types';

const statusMap: Record<number, { label: string; theme: string }> = {
  1: { label: '待平台审核', theme: 'warning' },
  2: { label: '平台审核通过', theme: 'success' },
  3: { label: '供应商已接单', theme: 'success' },
  4: { label: '已发货', theme: 'primary' },
  5: { label: '已完成', theme: 'default' },
  6: { label: '已取消', theme: 'default' },
  7: { label: '平台审核拒绝', theme: 'danger' },
  8: { label: '供应商拒单', theme: 'danger' },
};

export default function OrderAudit() {
  const [data, setData] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchStatus, setSearchStatus] = useState<number | undefined>(undefined);
  const [searchOrderNo, setSearchOrderNo] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [auditDialogVisible, setAuditDialogVisible] = useState(false);
  const [detailDialogVisible, setDetailDialogVisible] = useState(false);
  const [auditingOrder, setAuditingOrder] = useState<Order | null>(null);
  const [viewingOrder, setViewingOrder] = useState<Order | null>(null);
  const [auditRemark, setAuditRemark] = useState('');
  const [auditAction, setAuditAction] = useState<'pass' | 'reject'>('pass');

  // 结算批次相关状态
  const [settleBatches, setSettleBatches] = useState<OrderFeeSettleBatchVO[]>([]);
  const [feePaymentLoading, setFeePaymentLoading] = useState(false);
  const [feePaymentAuditVisible, setFeePaymentAuditVisible] = useState(false);
  const [feePaymentAuditing, setFeePaymentAuditing] = useState<OrderFeeSettleBatchVO | null>(null);
  const [feePaymentAuditAction, setFeePaymentAuditAction] = useState<'pass' | 'reject'>('pass');
  const [feePaymentAuditRemark, setFeePaymentAuditRemark] = useState('');

  // 批量选中 + 服务费比例调整
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [feeAdjustVisible, setFeeAdjustVisible] = useState(false);
  const [feeAdjustOrder, setFeeAdjustOrder] = useState<Order | null>(null);
  const [feePlatformRate, setFeePlatformRate] = useState('');
  const [feeBuyerRate, setFeeBuyerRate] = useState('');
  const [feeSupplierRate, setFeeSupplierRate] = useState('');
  const [feeAdjustRemark, setFeeAdjustRemark] = useState('');
  const [feeAdjustLoading, setFeeAdjustLoading] = useState(false);

  const formatPrice = (price?: number) => {
    if (price === undefined || price === null) return '¥0.00';
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 2,
    }).format(price);
  };

  const formatRatio = (fee?: number, total?: number) => {
    if (!fee || !total || total <= 0) return '-';
    const ratio = (fee / total) * 10000;
    return `${Math.round(ratio)}`;
  };

  const statusOptions = [
    { label: '全部状态', value: undefined },
    { label: '待平台审核', value: 1 },
    { label: '平台审核通过', value: 2 },
    { label: '供应商已接单', value: 3 },
    { label: '已发货', value: 4 },
    { label: '已完成', value: 5 },
    { label: '已取消', value: 6 },
    { label: '平台审核拒绝', value: 7 },
    { label: '供应商拒单', value: 8 },
  ];

  const settleStatusMap: Record<number, { label: string; theme: string }> = {
    0: { label: '未结算', theme: 'default' },
    1: { label: '待审核', theme: 'warning' },
    2: { label: '已结算', theme: 'success' },
    3: { label: '审核拒绝', theme: 'danger' },
  };

  const columns = [
    {
      colKey: 'selection',
      title: () => (
        <input
          type="checkbox"
          checked={data.length > 0 && selectedOrderIds.length === data.length}
          onChange={(e) => {
            if (e.target.checked) setSelectedOrderIds(data.map((o) => o.id));
            else setSelectedOrderIds([]);
          }}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
      width: 50,
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={selectedOrderIds.includes(row.id)}
          onChange={() => toggleSelectOrder(row.id)}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
    },
    { colKey: 'id', title: 'ID', width: 70 },
    { colKey: 'orderNo', title: '订单编号', width: 160 },
    { colKey: 'buyerCompanyName', title: '采购商', width: 120 },
    { colKey: 'supplierCompanyName', title: '供应商', width: 120 },
    {
      colKey: 'totalAmount',
      title: '订单总额',
      width: 110,
      cell: ({ row }: any) => formatPrice(row.totalAmount),
    },
    {
      colKey: 'contractAmount',
      title: '合同金额',
      width: 110,
      cell: ({ row }: any) => formatPrice(row.contractAmount),
    },
    {
      colKey: 'platformFee',
      title: '服务费',
      width: 120,
      cell: ({ row }: any) => (
        <div>
          <div className="font-bold text-amber-700 text-xs">{formatPrice(row.platformFee)}</div>
          <div className="text-[10px] text-amber-600/80">万分之{formatRatio(row.platformFee, row.totalAmount)}</div>
        </div>
      ),
    },
    {
      colKey: 'buyerFeeSettleStatus',
      title: '采购商结算',
      width: 90,
      cell: ({ row }: any) => {
        const s = settleStatusMap[row.buyerFeeSettleStatus ?? 0];
        return <Tag size="small" theme={s?.theme as any}>{s?.label}</Tag>;
      },
    },
    {
      colKey: 'supplierFeeSettleStatus',
      title: '供应商结算',
      width: 90,
      cell: ({ row }: any) => {
        const s = settleStatusMap[row.supplierFeeSettleStatus ?? 0];
        return <Tag size="small" theme={s?.theme as any}>{s?.label}</Tag>;
      },
    },
    {
      colKey: 'status',
      title: '状态',
      width: 100,
      cell: ({ row }: any) => {
        const status = statusMap[row.status];
        return <Tag size="small" theme={status?.theme as any}>{status?.label || '未知'}</Tag>;
      },
    },
    { colKey: 'createTime', title: '创建时间', width: 150 },
    {
      colKey: 'action',
      title: '操作',
      width: 200,
      cell: ({ row }: any) => (
        <Space>
          <Button
            theme="primary"
            variant="text"
            size="small"
            icon={<SearchIcon />}
            onClick={() => handleViewDetail(row)}
          >
            详情
          </Button>
          {row.status === 1 && (
            <>
              <Button
                theme="success"
                variant="text"
                size="small"
                icon={<CheckIcon />}
                onClick={() => handleAudit(row, 'pass')}
              >
                通过
              </Button>
              <Button
                theme="danger"
                variant="text"
                size="small"
                icon={<CloseIcon />}
                onClick={() => handleAudit(row, 'reject')}
              >
                拒绝
              </Button>
            </>
          )}
          {/* <Button
            theme="warning"
            variant="text"
            size="small"
            onClick={() => openFeeAdjustModal(row)}
          >
            调比例
          </Button> */}
        </Space>
      ),
    },
  ];

  const fetchData = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const res = await orderApi.getPageList({
        pageNum: page,
        pageSize,
        status: searchStatus,
      });
      if (res.code === 200 && res.data) {
        const list = res.data.list || res.data.records || [];
        // 前端按订单编号过滤
        const filtered = searchOrderNo.trim()
          ? list.filter((o) => o.orderNo?.includes(searchOrderNo.trim()))
          : list;
        setData(filtered);
        setPagination({
          current: res.data.pageNum || page,
          pageSize: res.data.pageSize || pageSize,
          total: res.data.total || 0,
        });
      } else {
        MessagePlugin.error(res.message || '加载数据失败');
      }
    } catch (err) {
      MessagePlugin.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStatus]);

  const handleSearch = () => {
    fetchData(1, pagination.pageSize);
  };

  const handleReset = () => {
    setSearchStatus(undefined);
    setSearchOrderNo('');
    setPagination({ ...pagination, current: 1 });
    setSelectedOrderIds([]);
    fetchData(1, pagination.pageSize);
  };

  const toggleSelectOrder = (orderId: number) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  // 打开调整服务费比例弹窗（单条）
  const openFeeAdjustModal = (order?: Order) => {
    setFeeAdjustOrder(order || null);
    if (order) {
      const total = order.totalAmount || 0;
      setFeePlatformRate(total > 0 ? ((order.platformFee || 0) / total * 10000).toFixed(0) : '');
      setFeeBuyerRate(total > 0 ? ((order.buyerFee || 0) / total * 10000).toFixed(0) : '');
      setFeeSupplierRate(total > 0 ? ((order.supplierFee || 0) / total * 10000).toFixed(0) : '');
    } else {
      setFeePlatformRate('');
      setFeeBuyerRate('');
      setFeeSupplierRate('');
    }
    setFeeAdjustRemark('');
    setFeeAdjustVisible(true);
  };

  // 提交服务费比例调整
  const handleFeeAdjustSubmit = async () => {
    const platformRate = parseFloat(feePlatformRate);
    const buyerRate = parseFloat(feeBuyerRate);
    const supplierRate = parseFloat(feeSupplierRate);

    if (isNaN(platformRate) || platformRate < 0 || platformRate > 10000) {
      MessagePlugin.error('平台服务费比例需在 0~10000 之间（万分之）');
      return;
    }
    if (isNaN(buyerRate) || buyerRate < 0 || buyerRate > 10000) {
      MessagePlugin.error('采购商服务费比例需在 0~10000 之间（万分之）');
      return;
    }
    if (isNaN(supplierRate) || supplierRate < 0 || supplierRate > 10000) {
      MessagePlugin.error('供应商服务费比例需在 0~10000 之间（万分之）');
      return;
    }

    setFeeAdjustLoading(true);
    try {
      if (feeAdjustOrder) {
        // 单条调整：只传比例计算后的服务费金额，buyerPayAmount/supplierReceiveAmount 由后端自动重算
        const total = feeAdjustOrder.totalAmount || 0;
        const res = await orderApi.updateOrderFee(feeAdjustOrder.id, {
          platformFee: +(total * platformRate / 10000).toFixed(2),
          buyerFee: +(total * buyerRate / 10000).toFixed(2),
          supplierFee: +(total * supplierRate / 10000).toFixed(2),
          remark: feeAdjustRemark,
        });
        if (res.code === 200) {
          MessagePlugin.success('服务费调整成功');
          setFeeAdjustVisible(false);
          fetchData();
        } else {
          MessagePlugin.error(res.message || '调整失败');
        }
      } else if (selectedOrderIds.length > 0) {
        // 批量调整：传比例（0~1），后端按各自订单总额计算
        const res = await orderApi.batchUpdateOrderFee({
          orderIds: selectedOrderIds,
          platformFeeRate: platformRate / 10000,
          buyerFeeRate: buyerRate / 10000,
          supplierFeeRate: supplierRate / 10000,
          remark: feeAdjustRemark,
        });
        if (res.code === 200) {
          MessagePlugin.success(`已批量调整 ${selectedOrderIds.length} 个订单的服务费比例`);
          setFeeAdjustVisible(false);
          setSelectedOrderIds([]);
          fetchData();
        } else {
          MessagePlugin.error(res.message || '批量调整失败');
        }
      }
    } catch (err: any) {
      MessagePlugin.error(err.message || '调整失败');
    } finally {
      setFeeAdjustLoading(false);
    }
  };

  const handleAudit = (order: Order, action: 'pass' | 'reject') => {
    setAuditingOrder(order);
    setAuditAction(action);
    setAuditRemark('');
    setAuditDialogVisible(true);
  };

  const handleAuditSubmit = async () => {
    if (!auditingOrder) return;

    try {
      const res = await orderApi.audit({
        orderId: auditingOrder.id,
        approved: auditAction === 'pass',
        remark: auditRemark,
      });

      if (res.code === 200) {
        MessagePlugin.success(auditAction === 'pass' ? '审核通过' : '审核拒绝');
        setAuditDialogVisible(false);
        fetchData();
      } else {
        MessagePlugin.error(res.message || '审核失败');
      }
    } catch (err) {
      MessagePlugin.error('审核失败');
    }
  };

  const handleViewDetail = (order: Order) => {
    setViewingOrder(order);
    setDetailDialogVisible(true);
    loadSettleBatches(order.id);
  };

  // 加载订单的转款凭证
  const loadSettleBatches = async (orderId: number) => {
    setFeePaymentLoading(true);
    try {
      const res = await orderApi.getFeePayments(orderId);
      if (res.code === 200 && res.data) {
        setSettleBatches(res.data);
      } else {
        setSettleBatches([]);
      }
    } catch (err) {
      setSettleBatches([]);
    } finally {
      setFeePaymentLoading(false);
    }
  };

  // 打开审核结算批次弹窗
  const handleAuditFeePayment = (fp: OrderFeeSettleBatchVO, action: 'pass' | 'reject') => {
    setFeePaymentAuditing(fp);
    setFeePaymentAuditAction(action);
    setFeePaymentAuditRemark('');
    setFeePaymentAuditVisible(true);
  };

  // 提交凭证审核
  const handleFeePaymentAuditSubmit = async () => {
    if (!feePaymentAuditing) return;
    try {
      const res = await orderApi.auditFeePayment({
        paymentId: feePaymentAuditing.id,
        approved: feePaymentAuditAction === 'pass',
        remark: feePaymentAuditRemark,
      });
      if (res.code === 200) {
        MessagePlugin.success(feePaymentAuditAction === 'pass' ? '凭证审核通过' : '凭证审核拒绝');
        setFeePaymentAuditVisible(false);
        if (viewingOrder) {
          loadSettleBatches(viewingOrder.id);
        }
      } else {
        MessagePlugin.error(res.message || '审核失败');
      }
    } catch (err) {
      MessagePlugin.error('审核失败');
    }
  };

  // 凭证状态渲染
  const getFeePaymentStatusTag = (status?: number) => {
    switch (status) {
      case 0:
        return <Tag theme="warning">待审核</Tag>;
      case 1:
        return <Tag theme="success">审核通过</Tag>;
      case 2:
        return <Tag theme="danger">审核拒绝</Tag>;
      default:
        return <Tag>未知</Tag>;
    }
  };

  const getPayerTypeLabel = (type?: number) => {
    switch (type) {
      case 1:
        return '供应商';
      case 2:
        return '采购商';
      default:
        return '-';
    }
  };

  const getFirstImage = (mainImage: string | string[] | undefined | null): string => {
    if (!mainImage) return 'https://via.placeholder.com/40';
    
    let firstUrl = '';
    if (Array.isArray(mainImage)) {
      firstUrl = mainImage[0] || '';
    } else if (typeof mainImage === 'string') {
      const urls = mainImage.split(',');
      firstUrl = urls.length > 0 ? urls[0].trim() : '';
    }
    
    return firstUrl || 'https://via.placeholder.com/40';
  };

  const renderOrderItems = (items?: OrderItem[]) => {
    if (!items || items.length === 0) return <div className="text-gray-400 text-sm">暂无商品明细</div>;
    return (
      <Table
        data={items}
        columns={[
          {
            colKey: 'productImage',
            title: '图片',
            width: 60,
            cell: ({ row }: any) => (
              <img
                src={getFirstImage(row.productImage)}
                alt={row.productName}
                className="w-8 h-8 rounded object-cover"
              />
            ),
          },
          { colKey: 'productName', title: '商品名称', ellipsis: true },
          {
            colKey: 'price',
            title: '单价',
            width: 110,
            cell: ({ row }: any) => formatPrice(row.price),
          },
          { colKey: 'quantity', title: '数量', width: 60 },
          {
            colKey: 'subtotal',
            title: '小计',
            width: 110,
            cell: ({ row }: any) => formatPrice(row.subtotal),
          },
        ]}
        rowKey="id"
        size="small"
        bordered
      />
    );
  };
  const handleDownloadFile = (contractFile: string) => {
    try {
      const fileInfo = JSON.parse(contractFile);
      let url = fileInfo.url;
      
      if (!url.startsWith('http')) {
        url = `${import.meta.env.VITE_API_BASE_URL}${url}`;
      }
      
      const a = document.createElement('a');
      a.href = url;
      a.download = fileInfo.originalName || 'download';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err: any) {
      alert(err.message || '下载失败');
    }
  };

  return (
    <div className="min-w-0 overflow-x-hidden">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">订单管理</h1>
      </div>

      {/* 搜索栏 */}
      <div className="bg-white rounded-lg p-4 mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">订单状态：</span>
          <Select
            value={searchStatus}
            onChange={(val) => setSearchStatus(val as number | undefined)}
            options={statusOptions}
            placeholder="请选择状态"
            style={{ width: 160 }}
            clearable
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">订单编号：</span>
          <Input
            value={searchOrderNo}
            onChange={(val) => setSearchOrderNo(val)}
            placeholder="请输入订单编号"
            style={{ width: 200 }}
          />
        </div>
        <Space>
          <Button theme="primary" onClick={handleSearch}>
            查询
          </Button>
          <Button theme="default" variant="outline" onClick={handleReset}>
            重置
          </Button>
        </Space>
      </div>

      {/* 批量操作栏 */}
      {selectedOrderIds.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-3 flex items-center justify-between">
          <span className="text-sm text-amber-800">
            已选择 <span className="font-bold">{selectedOrderIds.length}</span> 个订单
          </span>
          <Space>
            <Button theme="default" variant="outline" size="small" onClick={() => setSelectedOrderIds([])}>
              清空
            </Button>
            <Button theme="warning" size="small" onClick={() => openFeeAdjustModal()}>
              批量调整比例
            </Button>
          </Space>
        </div>
      )}

      <div className="bg-white rounded-lg overflow-x-auto">
        <Table
          data={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            onChange: (pageInfo: any) => fetchData(pageInfo.current, pageInfo.pageSize),
          }}
        />
      </div>

      {/* 审核对话框 */}
      <Dialog
        header={auditAction === 'pass' ? '审核通过' : '审核拒绝'}
        visible={auditDialogVisible}
        onClose={() => setAuditDialogVisible(false)}
        onConfirm={handleAuditSubmit}
        width={560}
      >
        <div className="py-3 space-y-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <div className="flex"><span className="text-gray-400 w-24 shrink-0">订单编号</span><span className="text-gray-700">{auditingOrder?.orderNo}</span></div>
            <div className="flex"><span className="text-gray-400 w-24 shrink-0">采购商</span><span className="text-gray-700">{auditingOrder?.buyerCompanyName || '-'}</span></div>
            <div className="flex"><span className="text-gray-400 w-24 shrink-0">供应商</span><span className="text-gray-700">{auditingOrder?.supplierCompanyName || '-'}</span></div>
            <div className="flex"><span className="text-gray-400 w-24 shrink-0">订单总额</span><span className="text-gray-700 font-medium">{formatPrice(auditingOrder?.totalAmount)}</span></div>
            <div className="flex"><span className="text-gray-400 w-24 shrink-0">平台服务费</span><span className="text-amber-700 font-medium">{formatPrice(auditingOrder?.platformFee)}</span></div>
            <div className="flex"><span className="text-gray-400 w-24 shrink-0">采购商实付</span><span className="text-gray-700">{formatPrice(auditingOrder?.buyerPayAmount)}</span></div>
            <div className="flex"><span className="text-gray-400 w-24 shrink-0">供应商实收</span><span className="text-gray-700">{formatPrice(auditingOrder?.supplierReceiveAmount)}</span></div>
            {auditingOrder?.platformAuditRemark && (
              <div className="flex col-span-2"><span className="text-gray-400 w-24 shrink-0">审核备注</span><span className="text-red-600">{auditingOrder.platformAuditRemark}</span></div>
            )}
          </div>
          <div>
            <div className="mb-1.5 text-sm font-medium">审核备注：</div>
            <Textarea
              value={auditRemark}
              onChange={(value) => setAuditRemark(value)}
              placeholder="请输入审核备注（可选）"
              rows={2}
            />
          </div>
        </div>
      </Dialog>

      {/* 订单详情对话框 */}
      <Dialog
        header="订单详情"
        visible={detailDialogVisible}
        onClose={() => setDetailDialogVisible(false)}
        footer={
          <div className="flex justify-end">
            <Button onClick={() => setDetailDialogVisible(false)}>关闭</Button>
          </div>
        }
        width={800}
      >
        <div className="py-3 space-y-4">
          {/* 基本信息 */}
          <div>
            <h4 className="text-sm font-bold text-gray-900 mb-2">基本信息</h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <div className="flex">
                <span className="text-gray-400 w-20 shrink-0">订单编号</span>
                <span className="text-gray-700">{viewingOrder?.orderNo}</span>
              </div>
              <div className="flex">
                <span className="text-gray-400 w-20 shrink-0">采购商</span>
                <span className="text-gray-700">{viewingOrder?.buyerCompanyName || '-'}</span>
              </div>
              <div className="flex">
                <span className="text-gray-400 w-20 shrink-0">供应商</span>
                <span className="text-gray-700">{viewingOrder?.supplierCompanyName || '-'}</span>
              </div>
              <div className="flex">
                <span className="text-gray-400 w-20 shrink-0">收货地址</span>
                <span className="text-gray-700">{viewingOrder?.receiveAddress || '-'}</span>
              </div>
              <div className="flex">
                <span className="text-gray-400 w-20 shrink-0">联系人</span>
                <span className="text-gray-700">{viewingOrder?.receiveContact || '-'}</span>
              </div>
              <div className="flex">
                <span className="text-gray-400 w-20 shrink-0">联系电话</span>
                <span className="text-gray-700">{viewingOrder?.receivePhone || '-'}</span>
              </div>
              <div className="flex">
                <span className="text-gray-400 w-20 shrink-0">状态</span>
                <span>
                  {viewingOrder?.status ? (
                    <Tag size="small" theme={statusMap[viewingOrder.status]?.theme as any}>
                      {statusMap[viewingOrder.status]?.label}
                    </Tag>
                  ) : '-'}
                </span>
              </div>
              <div className="flex">
                <span className="text-gray-400 w-20 shrink-0">创建时间</span>
                <span className="text-gray-700">{viewingOrder?.createTime || '-'}</span>
              </div>
              {viewingOrder?.platformAuditTime && (
                <div className="flex">
                  <span className="text-gray-400 w-20 shrink-0">审核时间</span>
                  <span className="text-gray-700">{viewingOrder.platformAuditTime}</span>
                </div>
              )}
              {viewingOrder?.platformAuditRemark && (
                <div className="flex col-span-2">
                  <span className="text-gray-400 w-20 shrink-0">审核备注</span>
                  <span className="text-red-600">{viewingOrder.platformAuditRemark}</span>
                </div>
              )}
            </div>
          </div>

          {/* 费用明细 */}
          <div>
            <h4 className="text-sm font-bold text-gray-900 mb-2">费用明细</h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <div className="flex">
                <span className="text-gray-400 w-24 shrink-0">订单总额</span>
                <span className="text-gray-700 font-medium">{formatPrice(viewingOrder?.totalAmount)}</span>
              </div>
              <div className="flex">
                <span className="text-gray-400 w-24 shrink-0">平台服务费</span>
                <span className="text-amber-700 font-medium">
                  {formatPrice(viewingOrder?.platformFee)}
                  <span className="text-xs text-amber-600 ml-1">(万分之{formatRatio(viewingOrder?.platformFee, viewingOrder?.totalAmount)})</span>
                </span>
              </div>
              <div className="flex">
                <span className="text-gray-400 w-24 shrink-0">采购商服务费</span>
                <span className="text-gray-700">{formatPrice(viewingOrder?.buyerFee)} <span className="text-gray-400 text-xs">(万分之{formatRatio(viewingOrder?.buyerFee, viewingOrder?.totalAmount)})</span></span>
              </div>
              <div className="flex">
                <span className="text-gray-400 w-24 shrink-0">供应商服务费</span>
                <span className="text-gray-700">{formatPrice(viewingOrder?.supplierFee)} <span className="text-gray-400 text-xs">(万分之{formatRatio(viewingOrder?.supplierFee, viewingOrder?.totalAmount)})</span></span>
              </div>
              <div className="flex">
                <span className="text-gray-400 w-24 shrink-0">采购商实付</span>
                <span className="text-gray-700 font-medium">{formatPrice(viewingOrder?.buyerPayAmount)}</span>
              </div>
              <div className="flex">
                <span className="text-gray-400 w-24 shrink-0">供应商实收</span>
                <span className="text-gray-700 font-medium">{formatPrice(viewingOrder?.supplierReceiveAmount)}</span>
              </div>
            </div>
          </div>
          {/* 线下合同金额，文件 */}
          {viewingOrder?.supplierFeeConfig && viewingOrder.supplierFeeConfig.feeBaseType === 1 && viewingOrder.contractAmount && (
            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-2">线下合同</h4>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                <div className="flex">
                  <span className="text-gray-400 w-24 shrink-0">合同金额</span>
                  <span className="text-gray-700 font-medium">{formatPrice(viewingOrder.contractAmount)}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-400 w-24 shrink-0">合同文件</span>
                  <button
                    onClick={() => handleDownloadFile(viewingOrder.contractFile)}
                    className="text-[#1c5bff] font-medium hover:underline ml-1 flex items-center gap-1"
                  >
                    {JSON.parse(viewingOrder.contractFile).originalName}
                    <Download className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 商品明细 */}
          <div>
            <h4 className="text-sm font-bold text-gray-900 mb-2">商品明细</h4>
            {renderOrderItems(viewingOrder?.items)}
          </div>

          {/* 服务费结算批次 */}
          <div>
            <h4 className="text-sm font-bold text-gray-900 mb-2">服务费结算记录</h4>
            {feePaymentLoading ? (
              <div className="text-gray-400 text-sm">加载中...</div>
            ) : settleBatches.length === 0 ? (
              <div className="text-gray-400 text-sm">暂无结算记录</div>
            ) : (
              <div className="space-y-2">
                {settleBatches.map((batch) => (
                  <div key={batch.id} className="border border-gray-100 rounded-lg p-2.5">
                    <div className="flex items-start gap-3">
                      <div className="flex flex-wrap gap-1.5 w-20">
                        {batch.paymentProof?.map((url, idx) => (
                          <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                            <img
                              src={url}
                              alt="支付凭证"
                              className="w-9 h-9 rounded object-cover border border-gray-200 hover:border-[#1c5bff] transition-colors"
                            />
                          </a>
                        ))}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          {getFeePaymentStatusTag(batch.status)}
                          <span className="text-xs text-gray-500">
                            {getPayerTypeLabel(batch.payerType)} · {batch.payerCompanyName || '-'} · {batch.orderCount || 0}个订单
                          </span>
                        </div>
                        <div className="text-sm text-gray-700">
                          结算金额：{formatPrice(batch.amount)}
                        </div>
                        {batch.payerRemark && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            备注：{batch.payerRemark}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-0.5">
                          提交时间：{batch.createTime || '-'}
                          {batch.auditTime && ` · 审核时间：${batch.auditTime}`}
                        </div>
                        {batch.auditRemark && (
                          <div className={`text-xs mt-0.5 ${batch.status === 2 ? 'text-red-500' : 'text-gray-500'}`}>
                            审核备注：{batch.auditRemark}
                          </div>
                        )}
                      </div>
                      {batch.status === 0 && (
                        <Space>
                          <Button
                            theme="success"
                            variant="text"
                            size="small"
                            icon={<CheckIcon />}
                            onClick={() => handleAuditFeePayment(batch, 'pass')}
                          >
                            通过
                          </Button>
                          <Button
                            theme="danger"
                            variant="text"
                            size="small"
                            icon={<CloseIcon />}
                            onClick={() => handleAuditFeePayment(batch, 'reject')}
                          >
                            拒绝
                          </Button>
                        </Space>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Dialog>

      {/* 审核结算批次弹窗 */}
      <Dialog
        header={feePaymentAuditAction === 'pass' ? '审核通过' : '审核拒绝'}
        visible={feePaymentAuditVisible}
        onClose={() => setFeePaymentAuditVisible(false)}
        onConfirm={handleFeePaymentAuditSubmit}
        width={480}
      >
        <div className="py-3 space-y-3">
          <div className="space-y-1.5 text-sm">
            <div className="flex"><span className="text-gray-400 w-20 shrink-0">结算单号</span><span className="text-gray-700">{feePaymentAuditing?.settleNo || feePaymentAuditing?.id || '-'}</span></div>
            <div className="flex"><span className="text-gray-400 w-20 shrink-0">付款方</span><span className="text-gray-700">{getPayerTypeLabel(feePaymentAuditing?.payerType)} · {feePaymentAuditing?.payerCompanyName || '-'}</span></div>
            <div className="flex"><span className="text-gray-400 w-20 shrink-0">订单数量</span><span className="text-gray-700">{feePaymentAuditing?.orderCount || 0} 个</span></div>
            <div className="flex"><span className="text-gray-400 w-20 shrink-0">结算金额</span><span className="text-gray-700 font-medium">{formatPrice(feePaymentAuditing?.amount)}</span></div>
            {feePaymentAuditing?.payerRemark && (
              <div className="flex"><span className="text-gray-400 w-20 shrink-0">付款备注</span><span className="text-gray-700">{feePaymentAuditing.payerRemark}</span></div>
            )}
          </div>
          <div>
            <div className="mb-1.5 text-sm font-medium">审核备注：</div>
            <Textarea
              value={feePaymentAuditRemark}
              onChange={(value) => setFeePaymentAuditRemark(value)}
              placeholder="请输入审核备注（可选）"
              rows={2}
            />
          </div>
        </div>
      </Dialog>

      {/* 调整服务费比例弹窗 */}
      <Dialog
        header={feeAdjustOrder ? '调整服务费比例' : `批量调整服务费比例（${selectedOrderIds.length}个订单）`}
        visible={feeAdjustVisible}
        onClose={() => setFeeAdjustVisible(false)}
        onConfirm={handleFeeAdjustSubmit}
        width={520}
      >
        <div className="py-3 space-y-4">
          {feeAdjustOrder && (
            <div className="text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
              订单编号：{feeAdjustOrder.orderNo} · 订单总额：{formatPrice(feeAdjustOrder.totalAmount)}
            </div>
          )}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">平台服务费比例（万分之）</label>
              <Input
                value={feePlatformRate}
                onChange={(val) => setFeePlatformRate(val)}
                placeholder="如 500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">采购商服务费比例（万分之）</label>
              <Input
                value={feeBuyerRate}
                onChange={(val) => setFeeBuyerRate(val)}
                placeholder="如 200"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">供应商服务费比例（万分之）</label>
              <Input
                value={feeSupplierRate}
                onChange={(val) => setFeeSupplierRate(val)}
                placeholder="如 300"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">调整备注（选填）</label>
            <Textarea
              value={feeAdjustRemark}
              onChange={(value) => setFeeAdjustRemark(value)}
              placeholder="请输入调整原因"
              rows={2}
            />
          </div>
          <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
            <p>提示：单位为万分之。例如 500 表示 5%（500/10000）。平台服务费 = 总额 × 平台比例 / 10000，采购商服务费 = 总额 × 采购商比例 / 10000，供应商服务费 = 总额 × 供应商比例 / 10000。</p>
            {!feeAdjustOrder && selectedOrderIds.length > 0 && (
              <p className="mt-1 text-amber-600">批量调整将按相同比例应用到所有选中的订单。</p>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}
